// persistenceHealth.js
// Tracks whether our IndexedDB writes are actually succeeding.
//
// vuex-persist's internal queue never clears its `_flushing` flag if a write
// promise rejects, so saving stalls silently forever (TXP-806). Fix: guardedSetItem()
// must ALWAYS fulfil, NEVER reject, so the queue keeps flushing.
//
// Security: only DOMException `name` and coarse storage totals (MB) are ever logged.
// No PHI, patient data, or key material.

// Delay before the single retry attempt.
const RETRY_DELAY_MS = 400

// Try once, retry once. Unbounded retries would recreate the stall we're fixing.
const MAX_ATTEMPTS = 2

// Throttle failure telemetry so a full-quota device doesn't post on every keystroke.
const TELEMETRY_MIN_INTERVAL_MS = 5 * 60 * 1000

// User-facing message shown when saves are failing.
export const SAVE_FAILURE_MESSAGE = 'Your recent changes are NOT being saved on this device. Sync now to protect your work. If you cannot sync, write down anything you have entered since your last sync before leaving this screen.'

// Kept out of Vuex: committing on every failure would loop (commit -> write -> failure).
let consecutiveFailures = 0
let lastTelemetryAt = 0
let telemetryInFlight = false

// Resolves after `ms` milliseconds, for the retry delay.
function delay(ms) {
  return new Promise(resolve => {
    setTimeout(resolve, ms)
  })
}

// Identifies the kind of storage error. Never throws.
export function classifyStorageError(error) {
  const name = error && error.name ? String(error.name) : 'UnknownError'

  // The three browser names for "out of space" (Chrome/Safari, Firefox, legacy).
  const isQuota = name === 'QuotaExceededError'
    || name === 'NS_ERROR_DOM_QUOTA_REACHED'
    || name === 'QUOTA_EXCEEDED_ERR'

  return { name, isQuota }
}

// Storage usage in MB and percent. Returns null if unsupported or on error. Never throws.
export async function readStorageEstimate() {
  try {
    if (!navigator.storage || !navigator.storage.estimate) return null

    const { usage, quota } = await navigator.storage.estimate()
    if (!quota) return null

    // Some browsers/privacy modes report quota but leave usage undefined; treat as 0
    // to avoid NaN in the telemetry string.
    const usedBytes = typeof usage === 'number' ? usage : 0

    return {
      usageMb: Math.round(usedBytes / 1048576),
      quotaMb: Math.round(quota / 1048576),
      percentUsed: Math.round((usedBytes / quota) * 100),
    }
  } catch (error) {
    return null
  }
}

// Requests persistent storage so data is less likely to be evicted under disk pressure.
// Safe to call at start-up. Never throws.
export async function requestPersistentStorage() {
  try {
    if (!navigator.storage || !navigator.storage.persist) return false

    const alreadyPersisted = navigator.storage.persisted
      ? await navigator.storage.persisted()
      : false

    if (alreadyPersisted) return true

    const granted = await navigator.storage.persist()
    console.log('[persistence] persistent storage granted:', granted)

    return granted
  } catch (error) {
    return false
  }
}

// Reports a save failure via the existing sync-log.json endpoint (reuses syncLogPost /
// syncLogApiSetup from src/store/index.js). Only sends error name, storage totals (MB),
// and attempt count — no patient data or key material. Never throws; best-effort only.
async function reportFailure(store, errorName, estimate, attempts) {
  try {
    const now = Date.now()

    // Skip while offline (normal state for this app) so we don't burn the throttle
    // window on a post that would fail anyway.
    if (!store.state.online) return

    // Throttle, so a device with a full quota does not post on every keystroke.
    if (telemetryInFlight) return
    if (now - lastTelemetryAt < TELEMETRY_MIN_INTERVAL_MS) return

    lastTelemetryAt = now
    telemetryInFlight = true

    const startTime = new Date()
    const details = estimate
      ? `usageMb=${estimate.usageMb} quotaMb=${estimate.quotaMb} percentUsed=${estimate.percentUsed}`
      : 'estimate unavailable'

    const payload = await store.dispatch('syncLogApiSetup', {
      status: 'Error',
      syncType: 'Persistence: Local Save Failure',
      startTime,
      endTime: new Date(),
      noteNum: 0,
      errors: `IndexedDB write rejected after ${attempts} attempt(s). error.name=${errorName}. ${details}`,
    })

    await store.dispatch('syncLogPost', payload)
  } catch (error) {
    // Deliberately swallowed. Telemetry is best-effort only.
  } finally {
    telemetryInFlight = false
  }
}

// Writes `value` to `storage` under `key`, with one retry and a user-visible failure report.
//
// CONTRACT: must always fulfil (never reject or throw), with `true` on success / `false`
// on failure. This is what keeps the vuex-persist queue from stalling — do not "simplify"
// by re-throwing or returning the raw storage promise (reintroduces TXP-806).
export async function guardedSetItem(storage, key, value, store) {
  let lastError = null

  // Skip the retry once we're already failing — it only adds delay to an already
  // backed-up, strictly-serialised queue.
  const alreadyFailing = !!(store && store.state && store.state.saveHealth && store.state.saveHealth.failed)
  const maxAttempts = alreadyFailing ? 1 : MAX_ATTEMPTS

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await storage.setItem(key, value)

      // Recovered from a prior failure: clear the banner and notify the provider.
      if (consecutiveFailures > 0) {
        consecutiveFailures = 0
        try {
          if (store && store.state && store.state.saveHealth && store.state.saveHealth.failed) {
            store.commit('clearSaveFailure')
            store.dispatch('notify', {
              value: 'Saving to this device has resumed.',
              color: 'success',
            })
          }
        } catch (commitError) {
          // Never let recovery reporting affect the return value.
        }
      }

      return true
    } catch (error) {
      lastError = error

      // Brief wait before the retry, to clear a momentary lock/aborted transaction.
      if (attempt < maxAttempts) {
        // eslint-disable-next-line no-await-in-loop
        await delay(RETRY_DELAY_MS)
      }
    }
  }

  // All attempts failed. Nothing below may throw.
  consecutiveFailures += 1

  const { name, isQuota } = classifyStorageError(lastError)

  // Dev-facing log only. No PHI, state contents, or key material.
  console.error(
    `[persistence] IndexedDB write to key "${key}" failed after ${maxAttempts} attempt(s).`,
    'error.name =', name,
    'isQuota =', isQuota,
    'consecutiveFailures =', consecutiveFailures,
  )

  // Raise the banner only on the healthy -> failed transition, to avoid a
  // commit -> write -> failure loop.
  try {
    if (store && store.state && store.state.saveHealth && !store.state.saveHealth.failed) {
      store.commit('setSaveFailure', {
        message: SAVE_FAILURE_MESSAGE,
        errorName: name,
        isQuota,
      })
    }
  } catch (commitError) {
    console.error('[persistence] could not raise the save-failure banner:', commitError && commitError.name)
  }

  // Fire-and-forget telemetry; not awaited, so it can't delay the queue.
  try {
    if (store) {
      readStorageEstimate()
        .then(estimate => reportFailure(store, name, estimate, maxAttempts))
        .catch(() => {})
    }
  } catch (telemetryError) {
    // Deliberately swallowed.
  }

  // Fulfil with false — never throw or re-throw `lastError`.
  return false
}

// Test seam for resetting module state. No current callers (no unit test runner yet),
// kept for when one exists.
// eslint-disable-next-line no-underscore-dangle
export function __resetPersistenceHealthForTests() {
  consecutiveFailures = 0
  lastTelemetryAt = 0
  telemetryInFlight = false
}
